import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { isSensitiveAnalyticsPathname } from '@bookkeeper/shared/capture-hosts';
import { shouldSkipInitialCapture, whenInitialCaptureReady } from '@/lib/initial-capture-ready';

/**
 * Captures `$pageview` on React-Router SPA navigations.
 *
 * The landing initializes PostHog with `capture_pageview: false`, and React
 * Router (unlike Next.js) has no built-in pageview, so client-side route changes
 * would otherwise go uncounted. PostHog gates `capture()` by consent state, so
 * this component is intentionally consent-agnostic:
 *   PENDING  → cookieless anonymous pageview after restore (`on_reject`)
 *   DECLINED → cookieless anonymous pageview
 *   ACCEPTED → full pageview
 *
 * Wait for initPostHog's geo+consent restore, then skip the pathname that
 * restore already covered with its explicit manual pageview.
 * A visitor who navigates during the geo wait is not double-counted.
 */
export function PostHogPageview() {
  const { pathname } = useLocation();
  // Only the first uncancelled settled effect may be covered by initialization.
  // A later visit to the same pathname is a distinct navigation.
  const firstSettledEffect = useRef(true);

  useEffect(() => {
    let cancelled = false;
    void whenInitialCaptureReady().then(() => {
      if (cancelled) return;
      const coveredStartupOccurrence = shouldSkipInitialCapture(
        pathname,
        firstSettledEffect.current
      );
      firstSettledEffect.current = false;
      if (coveredStartupOccurrence) return;
      if (isSensitiveAnalyticsPathname(pathname)) return;
      const url = `${window.location.origin}${pathname}`;
      posthog.capture('$pageview', { $current_url: url });
    });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}
